import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  Panel,
  MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { nodeTypes as customNodeTypes } from '../components/nodes/CustomNodes';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import { NodeConfigModal } from '../components/nodes/NodeConfigModal';
import { PageHeader } from '../components/PageHeader';

// Node Types Definition with descriptions
const nodeTypeDefinitions = {
  input: {
    label: 'Input Source',
    description: 'Import data from external sources',
    category: 'Data Sources'
  },
  filter: {
    label: 'Filter',
    description: 'Filter and validate data',
    category: 'Processing'
  },
  transform: {
    label: 'Transform',
    description: 'Transform and manipulate data',
    category: 'Processing'
  },
  agent: {
    label: 'AI Agent',
    description: 'Intelligent AI processing',
    category: 'AI'
  },
  output: {
    label: 'Output',
    description: 'Export or save results',
    category: 'Data Destinations'
  },
  api: {
    label: 'API Connection',
    description: 'Connect to external APIs',
    category: 'Integrations'
  },
  database: {
    label: 'Database',
    description: 'Database operations',
    category: 'Data Sources'
  },
  webhook: {
    label: 'Webhook',
    description: 'Handle webhook events',
    category: 'Integrations'
  },
  custom: {
    label: 'Custom Logic',
    description: 'Custom processing logic',
    category: 'Processing'
  },
  sort: {
    label: 'Sort',
    description: 'Sort data by specified columns',
    category: 'Data Processing'
  },
  join: {
    label: 'Join',
    description: 'Join two datasets together',
    category: 'Data Processing'
  },
  merge: {
    label: 'Merge',
    description: 'Merge multiple datasets',
    category: 'Data Processing'
  },
  aggregate: {
    label: 'Aggregate',
    description: 'Group and aggregate data',
    category: 'Data Processing'
  },
  distinct: {
    label: 'Distinct',
    description: 'Remove duplicate records',
    category: 'Data Processing'
  },
  decisionTree: {
    label: 'Decision Tree',
    description: 'Build a decision tree model',
    category: 'Advanced Analytics'
  },
  sqlBuilder: {
    label: 'SQL Builder',
    description: 'Generate SQL scripts dynamically',
    category: 'Advanced Analytics'
  },
  regression: {
    label: 'Regression',
    description: 'Perform regression analysis',
    category: 'Advanced Analytics'
  },
  clustering: {
    label: 'Clustering',
    description: 'Perform data clustering',
    category: 'Advanced Analytics'
  },
  timeSeries: {
    label: 'Time Series',
    description: 'Analyze time series data',
    category: 'Advanced Analytics'
  },
  dataValidation: {
    label: 'Data Validation',
    description: 'Validate data quality and integrity',
    category: 'Data Quality'
  },
  dataCleansing: {
    label: 'Data Cleansing',
    description: 'Clean and standardize data',
    category: 'Data Quality'
  },
  pivot: {
    label: 'Pivot',
    description: 'Create pivot tables from data',
    category: 'Data Transformation'
  },
  unpivot: {
    label: 'Unpivot',
    description: 'Convert columns to rows',
    category: 'Data Transformation'
  },
  transpose: {
    label: 'Transpose',
    description: 'Transpose rows and columns',
    category: 'Data Transformation'
  },
  storedProcedure: {
    label: 'Stored Procedure',
    description: 'Execute database stored procedures',
    category: 'Database Operations'
  },
  indexManager: {
    label: 'Index Manager',
    description: 'Manage database indexes',
    category: 'Database Operations'
  },
  schemaCompare: {
    label: 'Schema Compare',
    description: 'Compare database schemas',
    category: 'Database Operations'
  },
  
  // Email and Notification Nodes
  emailSender: {
    label: 'Email Sender',
    description: 'Send emails with data and reports',
    category: 'Notifications'
  },
  emailTemplate: {
    label: 'Email Template',
    description: 'Create reusable email templates',
    category: 'Notifications'
  },
  scheduler: {
    label: 'Scheduler',
    description: 'Schedule automated emails and reports',
    category: 'Notifications'
  },
  
  // Reporting and Dashboard Nodes
  dashboardGenerator: {
    label: 'Dashboard Generator',
    description: 'Generate interactive dashboards',
    category: 'Reporting'
  },
  chartBuilder: {
    label: 'Chart Builder',
    description: 'Create data visualizations and charts',
    category: 'Reporting'
  },
  reportExporter: {
    label: 'Report Exporter',
    description: 'Export reports in various formats',
    category: 'Reporting'
  },
  kpiTracker: {
    label: 'KPI Tracker',
    description: 'Track key performance indicators',
    category: 'Reporting'
  }
};

// Initial nodes for demonstration
const initialNodes = [
  {
    id: '1',
    type: 'input',
    data: { 
      label: 'Input Source',
      description: 'Start your workflow here'
    },
    position: { x: 250, y: 5 }
  },
];

const VisualPlayground = () => {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [lastAddedNode, setLastAddedNode] = useState(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [selectedEdges, setSelectedEdges] = useState([]);
  const [clipboard, setClipboard] = useState({ nodes: [], edges: [] });
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Color sets for light and dark modes
  const colorSets = {
    light: {
      background: 'bg-white',
      text: 'text-gray-900',
      textSecondary: 'text-gray-600',
      border: 'border-gray-200',
      panel: 'bg-white',
      panelShadow: 'shadow-lg',
      button: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
      buttonPrimary: 'bg-primary-600 hover:bg-primary-700 text-white',
      buttonOutline: 'border-primary-600 text-primary-600 hover:bg-primary-50',
      sidebar: 'bg-white',
      nodeItem: 'bg-white border-gray-200 hover:border-primary-500',
      nodeItemIcon: 'bg-primary-50',
      input: 'border-gray-300 focus:ring-primary-500 focus:border-primary-500',
      flowBackground: 'bg-gray-50',
      nodeTitle: 'text-gray-900',
    },
    dark: {
      background: 'bg-gray-900',
      text: 'text-gray-100',
      textSecondary: 'text-gray-400',
      border: 'border-gray-700',
      panel: 'bg-gray-800',
      panelShadow: 'shadow-xl shadow-black/20',
      button: 'bg-gray-700 hover:bg-gray-600 text-gray-200',
      buttonPrimary: 'bg-primary-700 hover:bg-primary-600 text-white',
      buttonOutline: 'border-primary-500 text-primary-400 hover:bg-gray-700',
      sidebar: 'bg-gray-800',
      nodeItem: 'bg-gray-800 border-gray-700 hover:border-primary-500',
      nodeItemIcon: 'bg-gray-700',
      input: 'border-gray-600 bg-gray-700 text-gray-200 focus:ring-primary-500 focus:border-primary-500',
      flowBackground: 'bg-gray-950',
      nodeTitle: 'text-gray-800',
    }
  };

  // Get current color set based on mode
  const colors = isDarkMode ? colorSets.dark : colorSets.light;

  const categories = ['All', 'Data Sources', 'Processing', 'AI', 'Data Destinations', 'Integrations', 'Data Processing', 'Advanced Analytics', 'Data Quality', 'Data Transformation', 'Database Operations', 'Notifications', 'Reporting'];

  // Toggle sidebar visibility
  const toggleSidebar = useCallback(() => {
    setIsSidebarVisible(prev => !prev);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  // Update fullscreen handler
  const toggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only process if we have an active flow instance
      if (!reactFlowInstance) return;

      // Copy: Ctrl+C or Cmd+C
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        if (selectedNodes.length > 0) {
          event.preventDefault();
          copySelectedNodes();
        }
      }
      
      // Paste: Ctrl+V or Cmd+V
      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        if (clipboard.nodes.length > 0) {
          event.preventDefault();
          pasteNodes();
        }
      }
      
      // Delete: Delete key
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        handleDeleteNodes();
      }
      
      // Undo: Ctrl+Z or Cmd+Z
      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.preventDefault();
        handleUndo();
      }
      
      // Redo: Ctrl+Y or Cmd+Y or Ctrl+Shift+Z or Cmd+Shift+Z
      if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.shiftKey && event.key === 'z'))) {
        event.preventDefault();
        handleRedo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [reactFlowInstance, selectedNodes, clipboard]);

  // Save current state to undo stack before making changes
  const saveCurrentStateToHistory = useCallback(() => {
    if (reactFlowInstance) {
      const currentState = reactFlowInstance.toObject();
      setUndoStack(prev => [...prev, currentState]);
      setRedoStack([]);
    }
  }, [reactFlowInstance]);

  // Copy selected nodes to clipboard
  const copySelectedNodes = useCallback(() => {
    if (selectedNodes.length === 0) return;
    
    // Find all edges between selected nodes
    const relevantEdges = edges.filter(edge => {
      const sourceNodeSelected = selectedNodes.some(node => node.id === edge.source);
      const targetNodeSelected = selectedNodes.some(node => node.id === edge.target);
      return sourceNodeSelected && targetNodeSelected;
    });
    
    setClipboard({
      nodes: selectedNodes.map(node => ({ ...node })),
      edges: relevantEdges.map(edge => ({ ...edge }))
    });
  }, [selectedNodes, edges]);

  // Paste nodes from clipboard
  const pasteNodes = useCallback(() => {
    if (clipboard.nodes.length === 0) return;
    
    saveCurrentStateToHistory();
    
    // Create new IDs for pasted nodes and track ID mapping
    const idMap = {};
    const newNodes = clipboard.nodes.map(node => {
      const newId = `${node.id}-copy-${Date.now()}`;
      idMap[node.id] = newId;
      
      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + 50,
          y: node.position.y + 50
        }
      };
    });
    
    // Create new edges with updated IDs
    const newEdges = clipboard.edges.map(edge => {
      const newSource = idMap[edge.source];
      const newTarget = idMap[edge.target];
      
      if (newSource && newTarget) {
        return {
          ...edge,
          id: `e${newSource}-${newTarget}`,
          source: newSource,
          target: newTarget
        };
      }
      return null;
    }).filter(Boolean);
    
    setNodes(nds => [...nds, ...newNodes]);
    setEdges(eds => [...eds, ...newEdges]);
  }, [clipboard, setNodes, setEdges, saveCurrentStateToHistory]);

  // Handle node deletion
  const handleDeleteNodes = useCallback(() => {
    if (selectedNodes.length === 0 && selectedEdges.length === 0) return;
    
    saveCurrentStateToHistory();
    
    // Remove selected nodes
    if (selectedNodes.length > 0) {
      const selectedNodeIds = selectedNodes.map(node => node.id);
      
      setNodes(nds => nds.filter(node => !selectedNodeIds.includes(node.id)));
      
      // Remove any edges connected to deleted nodes
      setEdges(eds => eds.filter(edge => 
        !selectedNodeIds.includes(edge.source) && !selectedNodeIds.includes(edge.target)
      ));
    }
    
    // Remove selected edges
    if (selectedEdges.length > 0) {
      const selectedEdgeIds = selectedEdges.map(edge => edge.id);
      setEdges(eds => eds.filter(edge => !selectedEdgeIds.includes(edge.id)));
    }
  }, [selectedNodes, selectedEdges, setNodes, setEdges, saveCurrentStateToHistory]);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    
    // Save current state to redo stack
    if (reactFlowInstance) {
      const currentState = reactFlowInstance.toObject();
      setRedoStack(prev => [...prev, currentState]);
    }
    
    // Pop the last state from undo stack
    const newUndoStack = [...undoStack];
    const lastState = newUndoStack.pop();
    setUndoStack(newUndoStack);
    
    // Apply the state
    if (lastState) {
      setNodes(lastState.nodes || []);
      setEdges(lastState.edges || []);
      if (reactFlowInstance && lastState.viewport) {
        const { x = 0, y = 0, zoom = 1 } = lastState.viewport;
        reactFlowInstance.setViewport({ x, y, zoom });
      }
    }
  }, [undoStack, redoStack, reactFlowInstance, setNodes, setEdges]);

  // Redo last undone action
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    
    // Save current state to undo stack
    if (reactFlowInstance) {
      const currentState = reactFlowInstance.toObject();
      setUndoStack(prev => [...prev, currentState]);
    }
    
    // Pop the last state from redo stack
    const newRedoStack = [...redoStack];
    const nextState = newRedoStack.pop();
    setRedoStack(newRedoStack);
    
    // Apply the state
    if (nextState) {
      setNodes(nextState.nodes || []);
      setEdges(nextState.edges || []);
      if (reactFlowInstance && nextState.viewport) {
        const { x = 0, y = 0, zoom = 1 } = nextState.viewport;
        reactFlowInstance.setViewport({ x, y, zoom });
      }
    }
  }, [undoStack, redoStack, reactFlowInstance, setNodes, setEdges]);

  // Handle multi-join functionality
  const handleMultiJoin = useCallback(() => {
    if (selectedNodes.length < 2) return;
    
    saveCurrentStateToHistory();
    
    // Create a join node
    const joinNodeId = `join-${Date.now()}`;
    
    // Calculate the average position of selected nodes
    const avgX = selectedNodes.reduce((sum, node) => sum + node.position.x, 0) / selectedNodes.length;
    const avgY = selectedNodes.reduce((sum, node) => sum + node.position.y, 0) / selectedNodes.length;
    
    // Create the join node below the average position
    const joinNode = {
      id: joinNodeId,
      type: 'join',
      position: { x: avgX, y: avgY + 150 },
      data: { 
        label: 'Join',
        description: 'Join multiple data sources',
        config: {
          joinType: 'Inner Join'
        }
      }
    };
    
    // Create edges from each selected node to the join node
    const newEdges = selectedNodes.map(node => ({
      id: `e${node.id}-${joinNodeId}`,
      source: node.id,
      target: joinNodeId,
      animated: true
    }));
    
    setNodes(nds => [...nds, joinNode]);
    setEdges(eds => [...eds, ...newEdges]);
    
    // Select the new join node and open its configuration
    setSelectedNode(joinNode);
    setIsConfigModalOpen(true);
  }, [selectedNodes, saveCurrentStateToHistory, setNodes, setEdges]);

  // Align nodes horizontally
  const alignNodesHorizontally = useCallback(() => {
    if (selectedNodes.length < 2) return;
    
    saveCurrentStateToHistory();
    
    // Calculate the average Y position
    const avgY = selectedNodes.reduce((sum, node) => sum + node.position.y, 0) / selectedNodes.length;
    
    // Update node positions
    setNodes(nds => 
      nds.map(node => {
        if (selectedNodes.some(selectedNode => selectedNode.id === node.id)) {
          return {
            ...node,
            position: {
              ...node.position,
              y: avgY
            }
          };
        }
        return node;
      })
    );
  }, [selectedNodes, saveCurrentStateToHistory, setNodes]);

  // Align nodes vertically
  const alignNodesVertically = useCallback(() => {
    if (selectedNodes.length < 2) return;
    
    saveCurrentStateToHistory();
    
    // Calculate the average X position
    const avgX = selectedNodes.reduce((sum, node) => sum + node.position.x, 0) / selectedNodes.length;
    
    // Update node positions
    setNodes(nds => 
      nds.map(node => {
        if (selectedNodes.some(selectedNode => selectedNode.id === node.id)) {
          return {
            ...node,
            position: {
              ...node.position,
              x: avgX
            }
          };
        }
        return node;
      })
    );
  }, [selectedNodes, saveCurrentStateToHistory, setNodes]);

  // Distribute nodes horizontally
  const distributeNodesHorizontally = useCallback(() => {
    if (selectedNodes.length < 3) return;
    
    saveCurrentStateToHistory();
    
    // Sort nodes by x position
    const sortedNodes = [...selectedNodes].sort((a, b) => a.position.x - b.position.x);
    
    // Get leftmost and rightmost positions
    const leftX = sortedNodes[0].position.x;
    const rightX = sortedNodes[sortedNodes.length - 1].position.x;
    
    // Calculate spacing
    const totalSpace = rightX - leftX;
    const spacing = totalSpace / (sortedNodes.length - 1);
    
    // Update node positions
    setNodes(nds => 
      nds.map(node => {
        const index = sortedNodes.findIndex(n => n.id === node.id);
        if (index > 0 && index < sortedNodes.length - 1) {
          return {
            ...node,
            position: {
              ...node.position,
              x: leftX + spacing * index
            }
          };
        }
        return node;
      })
    );
  }, [selectedNodes, saveCurrentStateToHistory, setNodes]);

  // Distribute nodes vertically
  const distributeNodesVertically = useCallback(() => {
    if (selectedNodes.length < 3) return;
    
    saveCurrentStateToHistory();
    
    // Sort nodes by y position
    const sortedNodes = [...selectedNodes].sort((a, b) => a.position.y - b.position.y);
    
    // Get topmost and bottommost positions
    const topY = sortedNodes[0].position.y;
    const bottomY = sortedNodes[sortedNodes.length - 1].position.y;
    
    // Calculate spacing
    const totalSpace = bottomY - topY;
    const spacing = totalSpace / (sortedNodes.length - 1);
    
    // Update node positions
    setNodes(nds => 
      nds.map(node => {
        const index = sortedNodes.findIndex(n => n.id === node.id);
        if (index > 0 && index < sortedNodes.length - 1) {
          return {
            ...node,
            position: {
              ...node.position,
              y: topY + spacing * index
            }
          };
        }
        return node;
      })
    );
  }, [selectedNodes, saveCurrentStateToHistory, setNodes]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true }, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type || !reactFlowInstance) return;

      // Save current state for undo
      saveCurrentStateToHistory();

      const position = reactFlowWrapper.current.getBoundingClientRect();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const coordinates = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: String(Date.now()),
        type,
        position: coordinates,
        data: { 
          label: nodeTypeDefinitions[type].label,
          description: nodeTypeDefinitions[type].description,
          config: {}
        }
      };

      // Add the new node
      setNodes((nds) => {
        // If this is the first dropped node (only initial input node exists)
        if (nds.length === 1 && nds[0].type === 'input') {
          // Create connection from input node to the new node
          setEdges((eds) => eds.concat({
            id: `e${nds[0].id}-${newNode.id}`,
            source: nds[0].id,
            target: newNode.id,
            animated: true
          }));
          setLastAddedNode(newNode);
          return nds.concat(newNode);
        }
        
        // For subsequent nodes, connect to the last added node if it exists
        if (lastAddedNode) {
          setEdges((eds) => eds.concat({
            id: `e${lastAddedNode.id}-${newNode.id}`,
            source: lastAddedNode.id,
            target: newNode.id,
            animated: true
          }));
        }
        
        return nds.concat(newNode);
      });

      // Store this as the last added node
      setLastAddedNode(newNode);
      
      // Open configuration modal for the new node
      setSelectedNode(newNode);
      setIsConfigModalOpen(true);
    },
    [reactFlowInstance, lastAddedNode, setNodes, setEdges, saveCurrentStateToHistory]
  );

  const onSave = useCallback(() => {
    if (reactFlowInstance) {
      const flow = reactFlowInstance.toObject();
      localStorage.setItem('visual-playground-flow', JSON.stringify(flow));
      // Show success message
      alert('Workflow saved successfully!');
    }
  }, [reactFlowInstance]);

  const onRestore = useCallback(() => {
    // Save current state for undo
    saveCurrentStateToHistory();
    
    const restoreFlow = async () => {
      const flow = JSON.parse(localStorage.getItem('visual-playground-flow'));
      if (flow) {
        const { x = 0, y = 0, zoom = 1 } = flow.viewport;
        setNodes(flow.nodes || []);
        setEdges(flow.edges || []);
        reactFlowInstance.setViewport({ x, y, zoom });
      }
    };
    restoreFlow();
  }, [reactFlowInstance, setNodes, setEdges, saveCurrentStateToHistory]);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setIsConfigModalOpen(true);
  }, []);

  const handleConfigUpdate = useCallback((nodeId, config) => {
    // Save current state for undo
    saveCurrentStateToHistory();
    
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                config
              }
            }
          : node
      )
    );
  }, [setNodes, saveCurrentStateToHistory]);

  const getAISuggestion = (node) => {
    if (!node || !node.type) return "Select a node to get AI suggestions";
    
    const suggestions = {
      input: "Connect this to a Filter node to clean your data before processing",
      filter: "Add transformation logic after filtering to prepare data for AI processing",
      transform: "Consider connecting this to an AI Agent for intelligent processing",
      agent: "Add error handling and output formatting after AI processing",
      output: "Make sure to validate data before final output",
      api: "Add rate limiting and error handling for API calls",
      database: "Consider caching frequently accessed data",
      webhook: "Add request validation and error handling",
      custom: "Document your custom logic for team collaboration"
    };
    return suggestions[node.type] || "Consider the flow of data through your workflow";
  };

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const filteredNodeTypes = Object.entries(nodeTypeDefinitions)
    .filter(([_, def]) => 
      (selectedCategory === 'All' || def.category === selectedCategory) &&
      (def.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
       def.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Visual Playground"
          subtitle="Design and prototype your AI workflows with our visual interface"
          actions={
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                className="p-2 text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={() => setIsFullScreen(!isFullScreen)}
                className="p-2 text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-2V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h4" />
                </svg>
              </button>
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-2 text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isDarkMode ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  )}
                </svg>
              </button>
            </div>
          }
        />

        {/* Main content */}
        <div className={`${isFullScreen ? 'fixed inset-0 z-50 ' + colors.background : 'relative h-[calc(100vh-16rem)]'} flex`}>
          {/* Sidebar with toggle button */}
          <div className={`
            ${isFullScreen ? 'h-full' : 'h-full'} 
            ${isSidebarVisible ? 'w-80' : 'w-0'} 
            transition-all duration-300 ease-in-out
            ${colors.sidebar} ${colors.border} border-r flex flex-col z-20 overflow-hidden
          `}>
            {/* Sidebar header with branding */}
            <div className={`p-3 ${colors.border} border-b flex flex-col`}>
              {isFullScreen && (
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">A</span>
                    </div>
                    <span className={`ml-2 text-sm font-semibold ${colors.text}`}>AURABLOX</span>
                  </div>
                  <button
                    onClick={toggleSidebar}
                    className={`p-1.5 rounded-md ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} ${colors.textSecondary} hover:text-gray-700`}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              <h2 className={`text-sm font-semibold ${colors.text}`}>Node Library</h2>
            </div>

            {/* Search and filters */}
            <div className={`p-3 space-y-3 ${colors.border} border-b`}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search nodes..."
                  className={`w-full pl-8 pr-3 py-1.5 text-sm ${colors.border} ${isDarkMode ? 'bg-gray-700 text-gray-200' : ''} rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg 
                  className={`absolute left-2.5 top-2 w-4 h-4 ${colors.textSecondary}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedCategory === category
                        ? 'bg-primary-100 text-primary-800'
                        : isDarkMode 
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Node list */}
            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-2">
                {filteredNodeTypes.map(([type, def]) => (
                  <div
                    key={type}
                    className={`${colors.nodeItem} rounded-lg p-3 shadow-sm cursor-move hover:shadow-md transition-all duration-200 border group relative overflow-hidden`}
                    onDragStart={(event) => onDragStart(event, type)}
                    draggable
                  >
                    {/* Hover effect background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Glow effect on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-transparent blur-xl"></div>
                    </div>

                    <div className="relative flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg ${customNodeTypes[type] ? (isDarkMode ? 'bg-gray-700' : 'bg-gray-50') : (isDarkMode ? 'bg-gray-700' : 'bg-primary-50')} flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                        {customNodeTypes[type]?.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm font-medium ${colors.text} group-hover:text-primary-500 transition-colors duration-200`}>{def.label}</h3>
                        <p className={`text-xs ${colors.textSecondary} line-clamp-1 group-hover:text-primary-400 transition-colors duration-200`}>{def.description}</p>
                      </div>
                      
                      {/* Drag indicator */}
                      <div className={`w-5 h-5 flex items-center justify-center ${colors.textSecondary} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Toggle sidebar button - only show when sidebar is hidden */}
          {!isSidebarVisible && (
            <button
              onClick={toggleSidebar}
              className={`absolute left-4 top-4 z-30 p-2 rounded-md ${colors.panel} ${colors.panelShadow} ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} group`}
            >
              <div className={`absolute left-full ml-2 px-2 py-1 ${colors.panel} rounded ${colors.panelShadow} invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap`}>
                <span className={`text-sm ${colors.textSecondary}`}>Open Node Library</span>
              </div>
              <svg className={`w-6 h-6 ${colors.textSecondary}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}

          {/* Main flow area */}
          <div className={`flex-1 h-full relative ${isDarkMode ? 'bg-gray-900' : ''}`} ref={reactFlowWrapper}>
            <ReactFlowProvider>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeClick={onNodeClick}
                nodeTypes={customNodeTypes}
                onInit={setReactFlowInstance}
                fitView
                snapToGrid={true}
                snapGrid={[15, 15]}
                multiSelectionKeyCode="Shift"
                selectionKeyCode="Control"
                onSelectionChange={(elements) => {
                  setSelectedNodes(elements.nodes);
                  setSelectedEdges(elements.edges);
                }}
                deleteKeyCode="Delete"
                edgesFocusable={true}
                selectNodesOnDrag={false}
                elementsSelectable={true}
                edgesUpdatable={true}
                edgeUpdaterRadius={10}
                edgeUpdaterType="default"
                proOptions={{ hideAttribution: true }}
                className={isDarkMode ? 'react-flow-dark-mode' : ''}
              >
                <Background color={isDarkMode ? '#444' : '#aaa'} gap={16} />
                <Controls className={isDarkMode ? 'react-flow-controls-dark' : ''} />
                <MiniMap 
                  nodeColor={isDarkMode ? '#555' : '#eee'}
                  maskColor={isDarkMode ? 'rgba(20, 20, 20, 0.7)' : 'rgba(240, 240, 240, 0.7)'}
                />
                <Panel position="top-right" className={`${colors.panel} p-4 rounded-lg ${colors.panelShadow} space-y-2 ${isFullScreen ? 'z-[90]' : 'z-20'}`}>
                  <button
                    className={`w-full ${colors.buttonPrimary} px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2`}
                    onClick={onSave}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    <span>Save Workflow</span>
                  </button>
                  <button
                    className={`w-full border ${colors.buttonOutline} px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2`}
                    onClick={onRestore}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Restore</span>
                  </button>
                  <button
                    className={`w-full ${colors.button} px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2`}
                    onClick={toggleFullScreen}
                  >
                    {isFullScreen ? (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9h6m-6 0v6m0-6L4 4m11 5h6m-6 0v6m0-6l5-5m0 11v5h-5m0 0l5-5m-11 0H4m0 0v5m0-5l5 5" />
                        </svg>
                        <span>Exit Fullscreen</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h4" />
                        </svg>
                        <span>Fullscreen</span>
                      </>
                    )}
                  </button>
                  
                  {/* Dark Mode Toggle - Only visible in fullscreen */}
                  {isFullScreen && (
                    <button
                      className={`w-full ${colors.button} px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2`}
                      onClick={toggleDarkMode}
                    >
                      {isDarkMode ? (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          <span>Light Mode</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                          </svg>
                          <span>Dark Mode</span>
                        </>
                      )}
                    </button>
                  )}
                </Panel>
                
                {/* Edit Toolbar */}
                <Panel position="top-left" className={`${colors.panel} p-2 rounded-lg ${colors.panelShadow} ${isFullScreen ? 'z-[90]' : 'z-20'} mt-2 ml-2`}>
                  <div className="flex space-x-1">
                    {/* Copy button */}
                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={copySelectedNodes}
                      disabled={selectedNodes.length === 0}
                      title="Copy (Ctrl+C)"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>

                    {/* Paste button */}
                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={pasteNodes}
                      disabled={clipboard.nodes.length === 0}
                      title="Paste (Ctrl+V)"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </button>

                    {/* Delete button */}
                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={handleDeleteNodes}
                      disabled={selectedNodes.length === 0}
                      title="Delete (Delete)"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>

                    <div className={`w-px h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'} my-auto mx-1`}></div>

                    {/* Undo button */}
                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={handleUndo}
                      disabled={undoStack.length === 0}
                      title="Undo (Ctrl+Z)"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                    </button>

                    {/* Redo button */}
                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={handleRedo}
                      disabled={redoStack.length === 0}
                      title="Redo (Ctrl+Y)"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                      </svg>
                    </button>

                    <div className={`w-px h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'} my-auto mx-1`}></div>

                    {/* Join button */}
                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={handleMultiJoin}
                      disabled={selectedNodes.length < 2}
                      title="Join Selected Nodes"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>

                    <div className={`w-px h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'} my-auto mx-1`}></div>

                    {/* Alignment buttons */}
                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={alignNodesHorizontally}
                      disabled={selectedNodes.length < 2}
                      title="Align Horizontally"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h16M8 8v8m8-8v8" />
                      </svg>
                    </button>

                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={alignNodesVertically}
                      disabled={selectedNodes.length < 2}
                      title="Align Vertically"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16M8 8h8m-8 8h8" />
                      </svg>
                    </button>

                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={distributeNodesHorizontally}
                      disabled={selectedNodes.length < 3}
                      title="Distribute Horizontally"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 5v14M19 5v14" />
                      </svg>
                    </button>

                    <button
                      className={`p-2 rounded-lg ${colors.textSecondary} disabled:opacity-50 disabled:cursor-not-allowed relative group overflow-hidden
                        ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      onClick={distributeNodesVertically}
                      disabled={selectedNodes.length < 3}
                      title="Distribute Vertically"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <svg className="w-5 h-5 relative transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 5h14M5 19h14" />
                      </svg>
                    </button>
                  </div>
                </Panel>

                {/* Status Bar */}
                <div className={`fixed bottom-4 right-4 flex items-center space-x-6 px-4 py-2 rounded-lg ${colors.panel} ${colors.panelShadow} ${colors.text} text-sm`} style={{ zIndex: 1000, marginRight: '120px' }}>
                  <div>
                    <span className="font-medium">Selected: </span>
                    <span>{selectedNodes.length + selectedEdges.length} items</span>
                  </div>
                  <div>
                    <span className="font-medium">Nodes: </span>
                    <span>{nodes.length}</span>
                  </div>
                  <div>
                    <span className="font-medium">Connections: </span>
                    <span>{edges.length}</span>
                  </div>
                  {aiSuggestion && (
                    <div className="ml-4 text-primary-500">
                      <span className="font-medium">AI: </span>
                      <span>{aiSuggestion}</span>
                    </div>
                  )}
                </div>
              </ReactFlow>
            </ReactFlowProvider>
          </div>

          {/* Configuration Modal */}
          <NodeConfigModal
            isOpen={isConfigModalOpen}
            onClose={() => setIsConfigModalOpen(false)}
            node={lastAddedNode}
            onUpdate={handleConfigUpdate}
            className={isDarkMode ? 'dark-mode' : ''}
            isDarkMode={isDarkMode}
            colors={colors}
          />

          {/* AI Assistant Modal - update z-index and styling for fullscreen */}
          <Transition appear show={isAIModalOpen} as={Fragment}>
            <Dialog
              as="div"
              className="fixed inset-0 z-[150]"
              onClose={() => setIsAIModalOpen(false)}
            >
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[150]" aria-hidden="true" />
              
              <div className="fixed inset-0 flex items-center justify-center p-4 z-[151]">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className={`relative w-full max-w-md transform overflow-hidden rounded-2xl ${colors.panel} p-6 text-left align-middle shadow-xl transition-all border ${colors.border}`}>
                    <div className="flex justify-between items-center mb-4">
                      <Dialog.Title as="h3" className={`text-lg font-medium ${colors.text}`}>
                        AI Assistant Suggestions
                      </Dialog.Title>
                      <button
                        onClick={() => setIsAIModalOpen(false)}
                        className={`${colors.textSecondary} hover:text-gray-500 focus:outline-none`}
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="mt-2">
                      <p className={`text-sm ${colors.textSecondary}`}>
                        {selectedNode ? getAISuggestion(selectedNode) : "Select a node to get AI suggestions"}
                      </p>
                    </div>

                    <div className="mt-6 flex justify-end">
                      <button
                        type="button"
                        className={`inline-flex justify-center rounded-md ${colors.buttonPrimary} px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors duration-200`}
                        onClick={() => setIsAIModalOpen(false)}
                      >
                        Got it, thanks!
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>
        </div>
      </div>
    </div>
  );
};

export { VisualPlayground }; 